import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { SpinnerDialog } from '@/components/ui/spinner-dialog'
import { UnauthorizedDialog } from '@/components/ui/unauthorized-dialog'
import { useAuthContext } from '@/contexts/auth-context'
import { useInvalidateToken } from '@/hooks/use-invalidate-token'
import { emitter } from '@/lib/emmiter'

type ShowUnauthorizedDialogProps = {
  value: boolean
  shouldInvalidateToken: boolean
}

type UnauthorizedContextValues = {
  openUnauthorizedDialog: boolean
  closeUnauthorizedDialog: () => void
  invalidateToken: () => Promise<void>
}

type UnauthorizedContextProviderProps = PropsWithChildren

export const UnauthorizedContext = createContext<UnauthorizedContextValues>(
  {} as UnauthorizedContextValues,
)

const UnauthorizedContextProvider = ({
  children,
}: UnauthorizedContextProviderProps) => {
  const [openUnauthorizedDialog, setOpenUnauthorizedDialog] = useState(false)
  const [openSpinnerDialog, setOpenSpinnerDialog] = useState(false)

  const { logout } = useAuthContext()
  const { mutation } = useInvalidateToken()

  const invalidateToken = useCallback(async () => {
    setOpenSpinnerDialog(true)

    await mutation.mutateAsync(undefined, {
      onSettled: () => setOpenSpinnerDialog(false),
    })
  }, [mutation])

  const closeUnauthorizedDialog = useCallback(() => {
    setOpenUnauthorizedDialog(false)
  }, [])

  useEffect(() => {
    const showUnauthorizedDialog = async ({
      value,
      shouldInvalidateToken,
    }: ShowUnauthorizedDialogProps) => {
      setOpenUnauthorizedDialog(value)

      if (shouldInvalidateToken) {
        await invalidateToken()
      }

      if (!value) {
        logout()
      }
    }

    emitter.on('show-unauthorized-dialog', showUnauthorizedDialog)

    return () => {
      emitter.off('show-unauthorized-dialog', showUnauthorizedDialog)
    }
  }, [invalidateToken, logout])

  return (
    <UnauthorizedContext.Provider
      value={{
        openUnauthorizedDialog,
        closeUnauthorizedDialog,
        invalidateToken,
      }}
    >
      {children}

      <UnauthorizedDialog
        open={openUnauthorizedDialog}
        onOpenChange={setOpenUnauthorizedDialog}
      />

      <SpinnerDialog
        open={openSpinnerDialog}
        onOpenChange={setOpenSpinnerDialog}
      />
    </UnauthorizedContext.Provider>
  )
}

function useUnauthorizedContext() {
  return useContext(UnauthorizedContext)
}

export { UnauthorizedContextProvider, useUnauthorizedContext }
